import React, { FunctionComponent } from 'react';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Grid, List, ListItem, Paper } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';

import { MultiValueBooleanInput } from '../../multi-value-controls/MultiValueBooleanInput';
import { MultiValueNumberInput } from '../../multi-value-controls/MultiValueNumberInput';
import { LoadSettingsControlProps } from '../LoadSettingsControls';
import { emphasizedControl } from './emphasizedControl';

interface BCLiveSettingsOptions extends LoadSettingsControlProps {
  roofOrFloor: 'roof' | 'floor';
}

export const RoofBCLiveSettings = (props: LoadSettingsControlProps) => <BCLiveSettings {...props} roofOrFloor="roof" />;
export const FloorBCLiveSettings = (props: LoadSettingsControlProps) => (
  <BCLiveSettings {...props} roofOrFloor="floor" />
);

const oneOrTwoFamilyDwellingHelpText =
  'Certain bottom chord live loads are mandated by IBC/IRC/FBC for one- or two-family dwellings. Checking this box' +
  ' will cause them to be generated automatically. These loads are in addition to anything supplied for the bottom' +
  ' chord live load.';
const useStorageLoadHelpText =
  "Generates concurrent bottom chord live loads if a 3' 6\" tall and 2' wide space exists along the bottom chord. The" +
  ' loads are 20 PSF where the space fits and 10 PSF where it does not fit. If there is nowhere it fits, storage' +
  ' loading is omitted. These loads are in addition to anything supplied for the bottom chord live load in the field' +
  ' above. You can uncheck this box if adjacent trusses do not support the storage load in the same positions, as' +
  ' detailed in the building code. There are also code-specific exceptions for which you might uncheck this; your' +
  ' code may except trusses where the insulation covers the bottom chord or where there is no attic access of at' +
  ' least 20"x30". Check your building code for more details.';
const nonConcurrentStorageLoadHelpText =
  'When unchecked, the 10 PSF load where the storage space does not fit is applied concurrently with the' +
  ' 20 PSF storage load, which is based on the building code. Checking this box applies them non-concurrently.';

const BCLiveSettings: FunctionComponent<BCLiveSettingsOptions> = ({
  roofOrFloor,
  disabled,
  loadSettingsArray,
  setLoadSettingsValues,
}) => (
  <>
    <Grid size={6}>
      <MultiValueNumberInput
        values={loadSettingsArray.map((loadSettings) =>
          roofOrFloor === 'roof' ? loadSettings.roofBottomChordLiveLoad : loadSettings.floorBottomChordLiveLoad,
        )}
        submit={(value) =>
          setLoadSettingsValues(
            roofOrFloor === 'roof' ? { roofBottomChordLiveLoad: value } : { floorBottomChordLiveLoad: value },
          )
        }
        label="BC Live Load"
        unit="psf"
        disabled={disabled}
      />
    </Grid>

    <Grid size={6}>
      <MultiValueBooleanInput
        values={loadSettingsArray.map((loadSettings) => loadSettings.applyBottomChordLiveLoadNonConcurrently)}
        onChange={(applyBottomChordLiveLoadNonConcurrently) =>
          setLoadSettingsValues({ applyBottomChordLiveLoadNonConcurrently })
        }
        label="Non-concurrent with other live loads"
        disabled={disabled}
      />
    </Grid>

    {roofOrFloor === 'roof' && (
      <Grid size={12}>
        <Paper sx={emphasizedControl}>
          <List dense>
            {loadSettingsArray.some((loadSettings) => loadSettings.oneOrTwoFamilyDwelling !== null) && (
              <ListItem disableGutters>
                <MultiValueBooleanInput
                  values={loadSettingsArray.map((loadSettings) => loadSettings.oneOrTwoFamilyDwelling ?? true)}
                  onChange={(value) =>
                    setLoadSettingsValues({ oneOrTwoFamilyDwelling: value, useFullStorageLoad: value })
                  }
                  label="One- or two-family dwelling"
                  disabled={disabled}
                />
                <TooltipIconButton title={oneOrTwoFamilyDwellingHelpText}>
                  <HelpIcon />
                </TooltipIconButton>
              </ListItem>
            )}
            <ListItem disableGutters>
              <MultiValueBooleanInput
                values={loadSettingsArray.map((loadSettings) => loadSettings.useFullStorageLoad)}
                onChange={(useFullStorageLoad) => setLoadSettingsValues({ useFullStorageLoad })}
                label="Use full storage load"
                disabled={
                  disabled || loadSettingsArray.some((loadSettings) => loadSettings.oneOrTwoFamilyDwelling === false)
                }
              />
              <TooltipIconButton title={useStorageLoadHelpText}>
                <HelpIcon />
              </TooltipIconButton>
            </ListItem>
            <ListItem disableGutters>
              <MultiValueBooleanInput
                values={loadSettingsArray.map((loadSettings) => loadSettings.applyReducedStorageLoadNonConcurrently)}
                onChange={(applyReducedStorageLoadNonConcurrently) =>
                  setLoadSettingsValues({ applyReducedStorageLoadNonConcurrently })
                }
                label="Apply 10 PSF non-concurrently with storage load"
                disabled={disabled || loadSettingsArray.some((loadSettings) => !loadSettings.useFullStorageLoad)}
              />
              <TooltipIconButton title={nonConcurrentStorageLoadHelpText}>
                <HelpIcon />
              </TooltipIconButton>
            </ListItem>
          </List>
        </Paper>
      </Grid>
    )}
  </>
);
